/**
 * Alpine.js module for nearby places accordion component.
 *
 * Provides grouping, type translation, distance formatting, and walking-time
 * calculation for POI data rendered in the shared nearby-places-accordion Blade component.
 */
export function nearbyPlacesAccordion() {
    return {
        /**
         * Format distance in meters to human-readable string.
         *
         * @param {number|null} meters - Distance in meters
         * @returns {string} Formatted distance (e.g. "350 metros" or "2,5 km")
         */
        formatDistance(meters) {
            if (!meters) return '';

            if (meters < 1000) {
                return `${Math.round(meters)} metros`;
            }
            return `${(meters / 1000).toFixed(1)} km`;
        },

        /**
         * Format distance in meters to walking time string.
         *
         * @param {number|null} meters - Distance in meters
         * @returns {string} Formatted walking time (e.g. "5 minutos caminando")
         */
        formatWalkingTime(meters) {
            if (!meters) return '';

            const minutes = Math.round(meters / 80);
            return `${minutes} minuto${minutes !== 1 ? 's' : ''} caminando`;
        },

        /**
         * Translate a POI type code to Spanish display name with pluralization.
         *
         * @param {string} type - The POI type code
         * @param {number} count - Number of items (used for pluralization)
         * @returns {string} Translated type name or raw type if unknown
         */
        translateType(type, count = 1) {
            const translations = {
                // Transporte
                'metro_station': count === 1 ? 'estación de Metro' : 'estaciones de Metro',
                'subway_station': count === 1 ? 'estación de Metro' : 'estaciones de Metro',
                'bus_stop': count === 1 ? 'paradero' : 'paraderos',
                'transit_station': count === 1 ? 'estación de transporte' : 'estaciones de transporte',
                'bus_station': count === 1 ? 'terminal de buses' : 'terminales de buses',
                'train_station': count === 1 ? 'estación de tren' : 'estaciones de tren',

                // Educación
                'university': count === 1 ? 'universidad' : 'universidades',
                'school': count === 1 ? 'colegio' : 'colegios',
                'secondary_school': count === 1 ? 'liceo' : 'liceos',
                'primary_school': count === 1 ? 'escuela' : 'escuelas',
                'kindergarten': count === 1 ? 'jardín infantil' : 'jardines infantiles',
                'preschool': count === 1 ? 'jardín' : 'jardines',

                // Salud
                'hospital': count === 1 ? 'hospital' : 'hospitales',
                'clinic': count === 1 ? 'clínica' : 'clínicas',
                'doctor': count === 1 ? 'centro médico' : 'centros médicos',
                'pharmacy': count === 1 ? 'farmacia' : 'farmacias',
                'health_center': count === 1 ? 'centro de salud' : 'centros de salud',
                'veterinary_care': count === 1 ? 'veterinaria' : 'veterinarias',
                'dentist': count === 1 ? 'dentista' : 'dentistas',
            };

            return translations[type] || type;
        },

        /**
         * Group places by type/category.
         *
         * @param {Array} places - Array of POI place objects
         * @returns {Object} Grouped places by type
         */
        groupPlacesByType(places) {
            if (!places || places.length === 0) return {};

            const grouped = {};
            places.forEach(place => {
                const type = place.type || place.category || 'Otro';
                if (!grouped[type]) {
                    grouped[type] = [];
                }
                grouped[type].push(place);
            });

            return grouped;
        }
    };
}

// Export singleton instance for use in Alpine x-data
export const nearbyPlacesAccordionModule = nearbyPlacesAccordion();